import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import type { UserMetadata } from '@/types/entities/user';
import type { UserProfile } from '@/types/auth';
import { authService } from '@/services/authService';

/**
 * @fileoverview useUserMetadata хук - ЭКСТРЕННАЯ ВЕРСИЯ
 * Использует authService вместо прямых запросов к Supabase для устранения зависания
 * ДОБАВЛЕНО: Простое кэширование для предотвращения множественных запросов
 */

// Simple cache to prevent multiple requests
let cachedUserMetadata: UserMetadata | null = null;
let cacheUserId: string | null = null;
let cachePromise: Promise<unknown> | null = null;

export const useUserMetadata = () => {
  const [userMetadata, setUserMetadata] = useState<UserMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  
  console.log('[useUserMetadata] EMERGENCY VERSION: user from useAuth:', user?.id);

  useEffect(() => {
    const fetchUserMetadata = async () => {
      console.log('[useUserMetadata] EMERGENCY MODE: Используем authService');
      
      if (!user?.id) {
        console.log('[useUserMetadata] No user found, clearing cache and skipping fetch');
        // Очищаем кэш при отсутствии пользователя
        cachedUserMetadata = null;
        cacheUserId = null;
        cachePromise = null;
        setUserMetadata(null);
        setLoading(false);
        return;
      }

      console.log('[useUserMetadata] User ID found:', user.id);
      
      // КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: Очищаем кэш при смене пользователя
      if (cacheUserId && cacheUserId !== user.id) {
        console.log('[useUserMetadata] ДИАГНОСТИКА - User changed, clearing cache. Old:', cacheUserId, 'New:', user.id);
        cachedUserMetadata = null;
        cacheUserId = null;
        cachePromise = null;
      }
      
      setLoading(true);
      setError(null);

      try {
        // Check cache first
        if (cachedUserMetadata && cacheUserId === user.id) {
          console.log('[useUserMetadata] Using cached profile for user:', user.id);
          setUserMetadata(cachedUserMetadata);
          setLoading(false);
          return;
        }
        
        // If there's already a pending request for this user, wait for it
        if (cachePromise && cacheUserId === user.id) {
          console.log('[useUserMetadata] Waiting for existing request for user:', user.id);
          const userProfile = await cachePromise as UserProfile | null;
          console.log('[useUserMetadata] ДИАГНОСТИКА - Cached promise resolved:', userProfile);
          
          if (userProfile) {
            // Маппим UserProfile → UserMetadata (так же как в основном блоке)
            const mappedMetadata: UserMetadata = {
              id: userProfile.id,
              userId: userProfile.userId,
              fullName: userProfile.fullName,
              email: userProfile.email,
              phone: userProfile.phone || '',
              role: userProfile.role,
              createdAt: new Date(userProfile.createdAt),
              updatedAt: new Date(userProfile.updatedAt || userProfile.createdAt),
              onboardingCompleted: userProfile.onboardingCompleted,
              onboardingCompletedAt: userProfile.onboardingCompletedAt ? new Date(userProfile.onboardingCompletedAt) : null,
              onboardingStep: userProfile.onboardingStep !== undefined ? String(userProfile.onboardingStep) : null
            };
            console.log('[useUserMetadata] ДИАГНОСТИКА - Mapped cached metadata:', mappedMetadata);
            
            // Обновляем кэш
            cachedUserMetadata = mappedMetadata;
            setUserMetadata(mappedMetadata);
          }
          setLoading(false);
          return;
        }
        
        // Create new request and cache the promise
        console.log('[useUserMetadata] Creating new request for user:', user.id);
        cacheUserId = user.id;
        
        console.log('[useUserMetadata] ДИАГНОСТИКА - Calling authService.loadUserProfile...');
        cachePromise = authService.loadUserProfile(user.id);
        console.log('[useUserMetadata] ДИАГНОСТИКА - Promise created, awaiting result...');
        
        const userProfile = await cachePromise as UserProfile | null;
        
        console.log('[useUserMetadata] ДИАГНОСТИКА - Promise resolved, result:', userProfile);
        console.log('[useUserMetadata] ДИАГНОСТИКА - Result type:', typeof userProfile);
        console.log('[useUserMetadata] ДИАГНОСТИКА - Result is null?', userProfile === null);
        console.log('[useUserMetadata] ДИАГНОСТИКА - Result is undefined?', userProfile === undefined);
        
        if (!userProfile) {
          console.error('[useUserMetadata] ДИАГНОСТИКА - authService вернул null, НЕ создаём emergency profile');
          throw new Error('Profile not found - authService returned null');
        }
        
        // Маппинг UserProfile → UserMetadata
        const mappedMetadata: UserMetadata = {
          id: userProfile.id,
          userId: userProfile.userId,
          fullName: userProfile.fullName,
          email: userProfile.email,
          phone: userProfile.phone || '',
          role: userProfile.role,
          createdAt: new Date(userProfile.createdAt),
          updatedAt: new Date(userProfile.updatedAt || userProfile.createdAt),
          onboardingCompleted: userProfile.onboardingCompleted,
          onboardingCompletedAt: userProfile.onboardingCompletedAt ? new Date(userProfile.onboardingCompletedAt) : null,
          onboardingStep: userProfile.onboardingStep !== undefined ? String(userProfile.onboardingStep) : null
        };
        
        console.log('[useUserMetadata] Mapped metadata:', mappedMetadata);
        
        // Cache the result
        cachedUserMetadata = mappedMetadata;
        cachePromise = null; // Clear the promise cache
        
        setUserMetadata(mappedMetadata);
        setLoading(false);
        
      } catch (error: unknown) {
        console.error('[useUserMetadata] ДИАГНОСТИКА - Error fetching user metadata:', {
          error,
          userId: user?.id,
          userEmail: user?.email,
          timestamp: new Date().toISOString()
        });
        setError(error instanceof Error ? error : new Error('Unknown error'));
        setLoading(false);
        
        // EMERGENCY BYPASS при ошибке
        const emergencyMetadata: UserMetadata = {
          id: user.id,
          userId: user.id,
          fullName: user.email?.split('@')[0] || 'User',
          email: user.email || 'user@example.com',
          phone: '',
          role: 'admin',
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        console.log('[useUserMetadata] Emergency metadata on error:', emergencyMetadata);
        setUserMetadata(emergencyMetadata);
      }
    };
    
    fetchUserMetadata();
  }, [user?.id]);
  
  return { userMetadata, loading, error };
};
